/**
 * Static catalog of every template token path known to the app, shared between the template
 * editor's placeholder picker and the email-sending placeholder menu so both offer the same set.
 *
 * Each mock object below mirrors a real context entity, but every leaf value is the dot-path
 * string that would resolve to it (e.g. `report.customerName`). Flattening the object therefore
 * yields the full list of valid, "resolves to a simple value" token paths.
 */
import { IInvoiceParty } from "@/models/invoice-party.entity";
import { Contact } from "@/models/user-contact.entity";
import { DamageTypeEnum } from "@/store/enum/damage-type.enum";
import { CoverageTypeEnum } from "@/store/enum/coverage-type.enum";
import { ProgressStatusEnum } from "@/store/enum/partner/progress.status.enum";
import { $t, te } from "@/lib/utility/t";

/**
 * Enum metadata discovered via enumToken() below. Each token path resolves to the enum name and
 * the enum object so translateTokenValue() can translate values without a second lookup table.
 */
const templateTokenEnumMeta: Record<
  string,
  {
    enumName: string;
    enumObject: Record<string, string>;
  }
> = {};

/**
 * Marks a token path as enum-backed: records it for translateTokenValue() and returns the path
 * unchanged, so it can be used as a drop-in replacement wherever a plain path string is assigned.
 */
function enumToken(path: string, enumName: string, enumObject: Record<string, string>): string {
  templateTokenEnumMeta[path] = { enumName, enumObject };
  return path;
}

function getInvoicePartyParts(prefix: string): Partial<IInvoiceParty> {
  return {
    name: prefix + ".name",
    email: prefix + ".email",
    phone: prefix + ".phone",
    taxnumber: prefix + ".taxnumber"
  };
}

function getTimestampPaths(prefix: string) {
  return {
    created: prefix + ".timestamp.created",
    lastModified: prefix + ".timestamp.lastModified",
    modified: prefix + ".timestamp.modified"
  };
}

function getTimestampsPaths(prefix: string) {
  return {
    created: prefix + ".timestamps.created",
    lastModified: prefix + ".timestamps.lastModified",
    modified: prefix + ".timestamps.modified"
  };
}

function getRecipientPaths(prefix: string) {
  return {
    firstName: prefix + ".recipient.firstName",
    lastName: prefix + ".recipient.lastName",
    companyName: prefix + ".recipient.companyName",
    email: prefix + ".recipient.email"
  };
}

function getRegistrationPaths(prefix: string) {
  return {
    city: prefix + ".registration.city",
    firstname: prefix + ".registration.firstname",
    firstregistrationDay: prefix + ".registration.firstregistrationDay",
    firstregistrationMonth: prefix + ".registration.firstregistrationMonth",
    firstregistrationYear: prefix + ".registration.firstregistrationYear",
    huMonth: prefix + ".registration.huMonth",
    huYear: prefix + ".registration.huYear",
    identificationnumber: prefix + ".registration.identificationnumber",
    manufacturerName: prefix + ".registration.manufacturerName",
    manufacturerType: prefix + ".registration.manufacturerType",
    manufacturerDescription: prefix + ".registration.manufacturerDescription",
    manufacturerNameCode: prefix + ".registration.manufacturerNameCode",
    manufacturerTypeCode: prefix + ".registration.manufacturerTypeCode",
    name: prefix + ".registration.name",
    numberplate: prefix + ".registration.numberplate",
    zipCode: prefix + ".registration.zipCode",
    street: prefix + ".registration.street",
    driveTyp: prefix + ".registration.driveTyp",
    vehicleClass: prefix + ".registration.vehicleClass"
  };
}

function getAddressWithGeoPaths(prefix: string) {
  return {
    street: `${prefix}.street`,
    zip: `${prefix}.zip`,
    city: `${prefix}.city`,
    state: `${prefix}.state`,
    countryCode: `${prefix}.countryCode`,
    geo: {
      lat: `${prefix}.geo.lat`,
      lng: `${prefix}.geo.lng`
    }
  };
}

const my = {
  firstName: "my.firstName",
  lastName: "my.lastName",
  userName: "my.userName",
  address: {
    street: "my.address.street",
    zip: "my.address.zip",
    city: "my.address.city",
    state: "my.address.state",
    countryCode: "my.address.countryCode"
  },
  contact: { email: "my.contact.email", phone: "my.contact.phone" }
};

const user = {
  firstName: "user.firstName",
  lastName: "user.lastName",
  userName: "user.userName",
  address: {
    street: "user.address.street",
    zip: "user.address.zip",
    city: "user.address.city",
    state: "user.address.state",
    countryCode: "user.address.countryCode"
  },
  contact: { email: "user.contact.email", phone: "user.contact.phone" },
  _id: "user._id"
};

const appraisal = {
  id: "appraisal.id",
  status: "appraisal.status",
  total: "appraisal.total",
  currency: "appraisal.currency",
  subTotal: "appraisal.subTotal",
  title: "appraisal.title",
  taxTotal: "appraisal.taxTotal",
  date: "appraisal.date",
  seller: getInvoicePartyParts("appraisal.seller"),
  totalLoss: "appraisal.totalLoss",
  residualValueNet: "appraisal.residualValueNet",
  replacementValueNet: "appraisal.replacementValueNet",
  diminishedValueNet: "appraisal.diminishedValueNet"
};

const partner = {
  id: "partner.id",
  companyName: "partner.companyName",
  address: {
    street: "partner.address.street",
    zip: "partner.address.zip",
    city: "partner.address.city",
    state: "partner.address.state",
    countryCode: "partner.address.countryCode",
    geo: { lat: "partner.address.geo.lat", lng: "partner.address.geo.lng" }
  },
  contact: { email: "partner.contact.email", phone: "partner.contact.phone" },
  settings: { defaultUrl: "partner.settings.defaultUrl" },
  companyUsername: "partner.companyUsername"
};

const report = {
  id: "report.id",
  partnerId: "report.partnerId",
  externalId: "report.externalId",
  customerName: "report.customerName",
  firstName: "report.firstName",
  lastName: "report.lastName",
  numberplate: "report.numberplate",
  customerAddress: {
    street: "report.customerAddress.street",
    zip: "report.customerAddress.zip",
    city: "report.customerAddress.city",
    state: "report.customerAddress.state"
  },
  customerContact: new Contact({ email: "report.customerContact.email", phone: "report.customerContact.phone" }),
  message: "report.message",
  datePreference: "report.datePreference",
  registrationResults: getRegistrationPaths("report"),
  progressStatus: enumToken("report.progressStatus", "ProgressStatusEnum", ProgressStatusEnum),
  broker: { name: "report.broker.name", number: "report.broker.number" },
  insurances: [
    {
      name: "report.insurances.0.name",
      number: "report.insurances.0.number",
      type: "report.insurances.0.type"
    }
  ],
  claimNumber: "report.claimNumber",
  title: "report.title",
  status: "report.status",
  statusMessage: "report.statusMessage",
  company: "report.company",
  isCompany: "report.isCompany",
  companyName: "report.companyName",
  companyCompany: { companyName: "report.companyCompany.companyName" },
  workShopCompany: {
    companyName: "report.workShopCompany.companyName",
    billingAddress: { street: "report.workShopCompany.billingAddress.street" }
  },
  appraiserCompany: { companyName: "report.appraiserCompany.companyName", street: "report.appraiserCompany.street" },
  insuranceCompany: {
    companyName: "report.insuranceCompany.companyName",
    street: "report.insuranceCompany.street"
  },
  leasingCompany: { companyName: "report.leasingCompany.companyName", street: "report.leasingCompany.street" },
  towingCompany: { companyName: "report.towingCompany.companyName", street: "report.towingCompany.street" },
  attorneyCompany: { companyName: "report.attorneyCompany.companyName", street: "report.attorneyCompany.street" },
  rentalCompany: { companyName: "report.rentalCompany.companyName", street: "report.rentalCompany.street" },
  damageBrokerCompany: {
    companyName: "report.damageBrokerCompany.companyName",
    street: "report.damageBrokerCompany.street"
  },
  insuranceBrokerCompany: {
    companyName: "report.insuranceBrokerCompany.companyName",
    street: "report.insuranceBrokerCompany.street"
  },
  isTaxDeductible: "report.isTaxDeductible",
  taxnumber: "report.taxnumber",
  language: "report.language",
  countryCode: "report.countryCode",
  marketingOptIn: "report.marketingOptIn",
  reportType: "report.reportType",
  damageType: enumToken("report.damageType", "DamageTypeEnum", DamageTypeEnum),
  coverageType: enumToken("report.coverageType", "CoverageTypeEnum", CoverageTypeEnum),
  coverage: "report.coverage",
  summary: "report.summary",
  isFixed: "report.isFixed",
  isDrivable: "report.isDrivable",
  isRoadworthy: "report.isRoadworthy",
  isUnderInfluence: "report.isUnderInfluence",
  isValidDriversLicense: "report.isValidDriversLicense",
  whatsAppContactOptIn: "report.whatsAppContactOptIn",
  liabilitySharePercent: "report.liabilitySharePercent",
  isDeductions: "report.isDeductions",
  isDepreciationCredit: "report.isDepreciationCredit",
  totalLoss: "report.totalLoss",
  criticality: "report.criticality",
  isWrapped: "report.isWrapped",
  isWrapDamaged: "report.isWrapDamaged",
  damageLocationDescription: "report.damageLocationDescription",
  repair: {
    status: "report.repair.status",
    isRentalProvided: "report.repair.isRentalProvided",
    notes: "report.repair.notes",
    startDate: "report.repair.startDate",
    endDate: "report.repair.endDate"
  },
  accidentDetails: {
    role: "report.accidentDetails.role",
    date: "report.accidentDetails.date",
    comment: "report.accidentDetails.comment",
    address: getAddressWithGeoPaths("report.accidentDetails.address")
  },
  policeDetails: {
    isRecorded: "report.policeDetails.isRecorded",
    department: "report.policeDetails.department",
    fileReference: "report.policeDetails.fileReference"
  },
  person: {
    firstName: "report.person.firstName",
    lastName: "report.person.lastName",
    primaryEmail: "report.person.primaryEmail",
    primaryPhone: "report.person.primaryPhone"
  }
};

const thg = {
  payoutConfiguration: {
    revenue: "thg.payoutConfiguration.revenue",
    isFixed: "thg.payoutConfiguration.isFixed"
  },
  status: "thg.status",
  bonus: "thg.bonus",
  impactType: "thg.impactType",
  impactFactor: "thg.impactFactor",
  registration: getRegistrationPaths("thg"),
  campaign: "thg.campaign",
  code: "thg.code",
  year: "thg.year",
  isMultiyear: "thg.isMultiyear",
  numberplate: "thg.numberplate",
  // Property is misspelled on the underlying model (IThg.kownFrom), so the token path must match
  // that spelling to resolve at render time.
  kownFrom: "thg.kownFrom",
  timestamp: getTimestampPaths("thg")
};

const billing = {
  billingNumber: "billing.billingNumber",
  type: "billing.type",
  isPublished: "billing.isPublished",
  userId: "billing.userId",
  accountNumber: "billing.accountNumber",
  timestamp: getTimestampPaths("billing")
};

const form = {
  partnerId: "form.partnerId",
  name: "form.name",
  contact: { email: "form.contact.email", phone: "form.contact.phone" },
  title: "form.title",
  message: "form.message",
  registrationResults: getRegistrationPaths("form"),
  timestamps: getTimestampsPaths("form")
};

const request = {
  title: "request.title",
  description: "request.description",
  recipient: getRecipientPaths("request"),
  validBy: "request.validBy",
  status: "request.status",
  documents: "request.documents",
  signatures: "request.signatures",
  timestamp: getTimestampPaths("request"),
  id: "request.id",
  partnerId: "request.partnerId"
};

const url = {
  request: "url.request",
  report: "url.report",
  sharing: "url.sharing",
  portalUrl: "url.portalUrl",
  booking: "url.booking",
  bookingCancel: "url.bookingCancel",
  bookingUpdate: "url.bookingUpdate"
};

const sign = {
  firstName: "sign.firstName",
  lastName: "sign.lastName",
  location: "sign.location",
  created: "sign.created"
};

const invoice = {
  id: "invoice.id",
  number: "invoice.number",
  status: "invoice.status",
  date: "invoice.date",
  due: "invoice.due",
  currency: "invoice.currency",
  total: "invoice.total",
  subTotal: "invoice.subTotal",
  taxTotal: "invoice.taxTotal",
  amountDue: "invoice.amountDue",
  totalFormatted: "invoice.totalFormatted",
  subTotalFormatted: "invoice.subTotalFormatted",
  buyer: getInvoicePartyParts("invoice.buyer"),
  seller: getInvoicePartyParts("invoice.seller")
};

const rental = {
  id: "rental.id",
  status: "rental.status",
  currency: "rental.currency",
  title: "rental.title",
  numberplate: "rental.numberplate",
  isPickup: "rental.isPickup",
  pickupDate: "rental.pickupDate",
  pickupLocation: getAddressWithGeoPaths("rental.pickupLocation"),
  isDelivery: "rental.isDelivery",
  deliveryDate: "rental.deliveryDate",
  deliveryLocation: getAddressWithGeoPaths("rental.deliveryLocation"),
  dailyRate: "rental.dailyRate",
  rate: "rental.rate",
  vehicleGroup: "rental.vehicleGroup",
  vehicleModel: "rental.vehicleModel",
  notes: "rental.notes",
  returnDate: "rental.returnDate",
  start: "rental.start",
  end: "rental.end",
  costCoveredByCustomer: "rental.costCoveredByCustomer",
  costCoveredByInsurance: "rental.costCoveredByInsurance",
  seller: getInvoicePartyParts("rental.seller"),
  timestamp: getTimestampPaths("rental")
};

const repairEstimate = {
  id: "repairEstimate.id",
  status: "repairEstimate.status",
  total: "repairEstimate.total",
  currency: "repairEstimate.currency",
  subTotal: "repairEstimate.subTotal",
  taxTotal: "repairEstimate.taxTotal",
  number: "repairEstimate.number",
  date: "repairEstimate.date",
  seller: getInvoicePartyParts("repairEstimate.seller"),
  timestamp: getTimestampPaths("repairEstimate")
};

const clearanceApproval = {
  id: "clearanceApproval.id",
  status: "clearanceApproval.status",
  currency: "clearanceApproval.currency",
  title: "clearanceApproval.title",
  description: "clearanceApproval.description",
  type: "clearanceApproval.type",
  total: "clearanceApproval.total",
  subTotal: "clearanceApproval.subTotal",
  taxTotal: "clearanceApproval.taxTotal",
  due: "clearanceApproval.due",
  seller: getInvoicePartyParts("clearanceApproval.seller"),
  timestamp: getTimestampPaths("clearanceApproval")
};

const clearance = {
  id: "clearance.id",
  status: "clearance.status",
  currency: "clearance.currency",
  title: "clearance.title",
  description: "clearance.description",
  repairStart: "clearance.repairStart",
  repairEnd: "clearance.repairEnd",
  total: "clearance.total",
  totalNet: "clearance.totalNet",
  subTotal: "clearance.subTotal",
  taxTotal: "clearance.taxTotal",
  seller: getInvoicePartyParts("clearance.seller"),
  timestamp: getTimestampPaths("clearance")
};

const lead = {
  id: "lead.id",
  title: "lead.title",
  description: "lead.description",
  numberplate: "lead.numberplate",
  externalId: "lead.externalId",
  status: "lead.status",
  due: "lead.due",
  firstName: "lead.firstName",
  lastName: "lead.lastName",
  email: "lead.email",
  phone: "lead.phone",
  partnerId: "lead.partnerId"
};

const person = {
  firstName: "person.firstName",
  lastName: "person.lastName",
  primaryEmail: "person.primaryEmail",
  primaryPhone: "person.primaryPhone"
};

const vehicle = {
  id: "vehicle.id",
  numberplate: "vehicle.numberplate"
};

const booking = {
  id: "booking.id",
  date: "booking.date",
  oldDate: "booking.oldDate",
  durationFormatted: "booking.durationFormatted",
  serviceDisplayName: "booking.serviceDisplayName",
  cost: "booking.cost",
  salutation: "booking.salutation",
  firstName: "booking.firstName",
  lastName: "booking.lastName",
  email: "booking.email",
  phone: "booking.phone",
  numberPlate: "booking.numberPlate",
  notes: "booking.notes",
  serviceLocation: "booking.serviceLocation",
  street: "booking.street",
  zip: "booking.zip",
  city: "booking.city",
  state: "booking.state"
};

const templateTokenContext = {
  my,
  user,
  appraisal,
  partner,
  report,
  thg,
  billing,
  form,
  request,
  url,
  sign,
  invoice,
  rental,
  repairEstimate,
  clearanceApproval,
  clearance,
  lead,
  person,
  vehicle,
  booking
};

/** Recursively collects leaf (non-object) values from a mock catalog object/array. */
function getLeafValues(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.flatMap(getLeafValues);
  }
  if (value && typeof value === "object") {
    return Object.values(value).flatMap(getLeafValues);
  }
  return typeof value === "string" ? [value] : [];
}

/** Full, static list of every template token path known to the app (e.g. "report.customerName"). */
export function getAllTemplateTokens(): string[] {
  return getLeafValues(templateTokenContext);
}

/**
 * Translates a token path (e.g. "report.customerName") to its human-readable label, falling back
 * to the raw path if no translation exists.
 */
export function getTemplateTokenLabel(tokenPath: string): string {
  const prefix = "components.template.editor.placeholder.path";
  const key = `${prefix}.${tokenPath}`;
  return te(key) ? $t(key) : tokenPath;
}

/**
 * Translates a resolved token value if its token path is a known enum-backed field and the value
 * is a valid member of that enum. Falls back to the raw value otherwise (unknown path, unknown
 * value, or missing locale key).
 */
export function translateTokenValue(tokenPath: string, value: string): string {
  const meta = templateTokenEnumMeta[tokenPath];
  if (!meta) return value;

  const { enumName, enumObject } = meta;
  if (!Object.values(enumObject).includes(value)) return value;

  const key = `enums.${enumName}.${value}`;
  return te(key) ? $t(key) : value;
}
